import { Component, input, output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '../../../../auth/auth.service';
import { UserDto } from '../../../../auth/user.dto';
import { CommentComponent } from '../../../../shared/comment/comment.component';
import { ConfirmationComponent } from '../../../../shared/confirmation/confirmation.component';
import { DynamicFormComponent } from '../../../../shared/dynamic-form/dynamic-form.component';
import { FieldOptions } from '../../../../shared/dynamic-form/interfaces/field-options.interface';
import { ContractEngagementType } from '../../../../shared/models/enums/contract-engagement-type.enum';
import { ContractStatus } from '../../../../shared/models/enums/contract-status.enum';
import { TPermission, TPermissionContext } from '../../../../shared/permissions/permissions';
import { PermissionService } from '../../../../shared/permissions/services/permission.service';
import { ContractsService } from '../../../../shared/services/contracts.service';
import { SignatureComponent } from '../../../../shared/signature/signature.component';
import { ToastService } from '../../../../shared/toast/toast.service';
import { ContractEngagementTraceabilityStatus } from '../../../../shared/traceability/contract-engagement-status.enum';
import { TraceabilityDto } from '../../../../shared/traceability/traceability.dto';
import { DateValidators } from '../../../../shared/validators/date.validators';
import { BeneficiaryDto } from '../../../dto/beneficiary.dto';
import { AssessmentDto } from '../../assessment/assessment-details/assessment.dto';
import { AssessmentService } from '../../assessment/assessment.service';
import { ContractActionConstraintDto, ContractActionDto, ContractActionTypeDto } from '../contract-action.dto';
import { DatesNewContractDto } from '../contract-details/dates-new-contract-dto';
import { ContratBeneficiaryStatus } from '../contract-enum';
import { ContractDto } from '../contract.dto';

@Component({
  selector: 'app-contract-engagement-details',
  templateUrl: './contract-engagement-details.component.html',
  styleUrl: './contract-engagement-details.component.scss',
  standalone: false,
})
export class ContractEngagementDetailsComponent {
  public contractForm: UntypedFormGroup;
  public submitted: boolean;

  beneficiary = input.required<BeneficiaryDto>();
  contract = input<ContractDto>();
  contractTypeSelected = input.required<ContractEngagementType>();

  closeModal = output<void>();

  StatutBeneficiaire = ContratBeneficiaryStatus;
  traceabilityStatus = ContractEngagementTraceabilityStatus;
  assessment: AssessmentDto | null = null;
  recap:
    | {
        fulfilled: boolean;
        unfulfilledDetail: string;
        actionType: string;
      }[]
    | null = null;

  @ViewChild('actions', { static: false }) public actionsForm: DynamicFormComponent;
  public actionsData: ContractActionDto[] = [];
  fieldActions: FieldOptions[] = [
    { value: 'id', validators: [] },
    { value: 'constraint', validators: [Validators.required] },
    { value: 'actionType', validators: [Validators.required] },
    { value: 'deadline', validators: [Validators.required, DateValidators.dateFormatValidator] },
    { value: 'place', validators: [Validators.maxLength(30)] },
    { value: 'fulfilled', validators: [] },
    { value: 'unfulfilledDetail', validators: [Validators.maxLength(30)] },
  ];

  public constraints: ContractActionConstraintDto[] = [];
  get actionsType(): ContractActionTypeDto[] {
    return this.constraints.map((c) => c.types).flat();
  }
  workflowedContractPermissionContext: TPermissionContext;

  permisions: TPermission[];

  // *********************************************************************************************
  // * NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG *
  // * NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG NG *
  // *********************************************************************************************

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly contractsService: ContractsService,
    private readonly toastService: ToastService,
    private readonly assessmentService: AssessmentService,
    private readonly modalService: NgbModal,
    private readonly permissionService: PermissionService,
    private readonly authService: AuthService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.workflowedContractPermissionContext =
      this.contractTypeSelected() === ContractEngagementType.EngagementResumptionStudies ? 'CE_REPRISE' : 'CE';
    this.constraints = await this.contractsService.findAllConstraints();

    await this.loadForm();

    let previousContrat: ContractDto | null = await this.contractsService.findPrevious(this.beneficiary().id, this.contract()?.id);
    if (previousContrat) {
      this.recap = previousContrat.actions.map((action) => ({
        fulfilled: action.fulfilled,
        unfulfilledDetail: action.unfulfilledDetail,
        actionType: this.actionsType.find((a) => a.id === action.actionTypeId).label,
      }));
    }

    if (this.contract()?.assessmentId) {
      this.assessment = await this.assessmentService.findById(this.contract()?.assessmentId);
    }

    if (!this.contract()?.id) {
      const { beginDateContractEngagement, minBeginDateContractEngagement, contractStillActive }: DatesNewContractDto =
        await this.contractsService.getDatesContract(this.beneficiary().id);
      this.contractForm.addValidators((group: FormGroup) => {
        if (minBeginDateContractEngagement && minBeginDateContractEngagement >= group.controls['beginDate'].value) {
          return { overrideExistingContrat: minBeginDateContractEngagement };
        } else if (contractStillActive) {
          return { contratStillActive: true };
        }
        return {};
      });
      this.contractForm.controls.beginDate.setValue(beginDateContractEngagement);
    }
    this.setDisable();
  }

  // *********************************************************************************************
  // * FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORMFORM FORM FORM FORM FORM FORM  *
  // * FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORMFORM FORM FORM FORM FORM FORM  *
  // *********************************************************************************************

  private async loadForm(): Promise<void> {
    this.contractForm = this.formBuilder.group(
      {
        beginDate: [this.contract()?.beginDate, [Validators.required, DateValidators.dateFormatValidator]],
        endDate: [{ value: this.contract()?.endDate, disabled: !this.contract()?.id }, [DateValidators.dateFormatValidator]],
        checkConditions: this.formBuilder.group({
          quarterlyDeclarationResources: [this.contract()?.quarterlyDeclarationResources, [Validators.required]],
          professionalResources: [this.contract()?.professionalResources, [Validators.required]],
          otherResources: [this.contract()?.otherResources, [Validators.required]],
          verificationFamilySituation: [this.contract()?.verificationFamilySituation, [Validators.required]],
          verificationCAFAddress: [this.contract()?.verificationCAFAddress, [Validators.required]],
          rsaIncreased: [this.contract()?.rsaIncreased, [Validators.required]],
          beneficiaryStatus: [this.contract()?.beneficiaryStatus, [Validators.required]],
        }),
        comment: [this.contract()?.comment],
      },
      {
        validators: [DateValidators.datesOrderValidator('beginDate', 'endDate')],
      },
    );

    if (this.contract() && this.contract().actions) {
      this.contract().actions.forEach((action) => {
        let constraint: ContractActionConstraintDto = this.constraints.find((c) => c.id === action.constraintId)!;
        this.actionsData.push({
          id: action.id,
          constraintId: action.constraintId,
          actionTypeId: action.actionTypeId,
          constraint: constraint,
          actionType: constraint.types.find((actionType) => actionType.id === action.actionTypeId)!,
          deadline: action.deadline,
          fulfilled: action.fulfilled,
          place: action.place,
          unfulfilledDetail: action.unfulfilledDetail,
        });
      });
    }
  }

  private async setDisable(): Promise<void> {
    const currentUser: UserDto = await this.authService.getUser();
    this.permisions = this.permissionService.getPermissions(currentUser, this.contract(), 'CE');
    if (!this.permisions.includes('CAN_EDIT')) {
      this.contractForm.disable();
      this.actionsForm.items.disable();
      this.actionsForm.disabled = true;
      if (this.permisions.includes((this.workflowedContractPermissionContext + '_CAN_EDIT_ACTION') as any)) {
        this.contractForm.controls.endDate.enable();
        this.actionsForm.items.controls.forEach((group: FormGroup) => {
          group.controls.fulfilled.enable();
          group.controls.unfulfilledDetail.enable();
          group.updateValueAndValidity();
        });
      }
    }
  }

  handleContraintChange(group: UntypedFormGroup) {
    group.controls['action'].setValue(null);
  }

  get checkConditionsForm(): UntypedFormGroup {
    return this.contractForm.controls.checkConditions as UntypedFormGroup;
  }

  // *********************************************************************************************
  // * ACTION ACTION ACTION ACTION ACTION ACTION ACTION ACTION ACTION ACTION ACTION ACTION ACTIO *
  // * ACTION ACTION ACTION ACTION ACTION ACTION ACTION ACTION ACTION ACTION ACTION ACTION ACTIO *
  // *********************************************************************************************

  public async onSubmit() {
    this.submitted = true;
    if (!this.contractForm.valid || !this.actionsForm.form.valid) {
      this.toastService.show('La sauvegarde a échoué, veuillez vérifier les données.', 'danger');
      return;
    }

    this.actionsForm.form.updateValueAndValidity();

    const toSaveContract = new ContractDto(
      this.contract() ? this.contract().id : undefined,
      this.contractForm.controls.beginDate.value,
      this.contractForm.controls.endDate.value || null,
      this.beneficiary().id,
      this.contractTypeSelected(),
      ContractStatus.Creation,
      {
        quarterlyDeclarationResources: this.checkConditionsForm.value.quarterlyDeclarationResources,
        professionalResources: this.checkConditionsForm.value.professionalResources,
        otherResources: this.checkConditionsForm.value.otherResources,
        verificationFamilySituation: this.checkConditionsForm.value.verificationFamilySituation,
        verificationCAFAddress: this.checkConditionsForm.value.verificationCAFAddress,
        rsaIncreased: this.checkConditionsForm.value.rsaIncreased,
        beneficiaryStatus: this.checkConditionsForm.value.beneficiaryStatus,
        actions: this.actionsForm.form.getRawValue().items.map((action) => ({
          id: action.id,
          constraintId: action.constraint.id,
          actionTypeId: action.actionType.id,
          deadline: action.deadline,
          fulfilled: action.fulfilled,
          place: action.place,
          unfulfilledDetail: action.unfulfilledDetail,
        })),
        comment: this.contractForm.value.comment,
        assessmentId: this.contract()?.assessmentId,
      },
    );
    if (this.contract()?.id) {
      await this.updateContract(toSaveContract);
    } else {
      await this.createContract(toSaveContract);
    }
  }

  public async createContract(toSaveContract: ContractDto) {
    try {
      await this.contractsService.saveContract(toSaveContract);
      this.toastService.show('Le contrat a été ajouté avec succès.', 'success');
      this.closeModal.emit();
    } catch (error) {
      if (error.status === 470) {
        this.toastService.show("Impossible de créer le contrat, le bénéficiaire n'a pas d'accompagnement.", 'danger');
      } else if (error.status === 471) {
        this.toastService.show("Impossible de créer le contrat, le bénéficiaire n'a pas de référent.", 'danger');
      } else if (error.status === 472) {
        this.toastService.show("Impossible de créer le contrat, le bénéficiaire n'a pas de structure.", 'danger');
      } else if (error.status === 473) {
        this.toastService.show(`Création impossible, un contrat non terminé existe déjà sur la période.`, 'danger');
      } else if (error.status === 474) {
        this.toastService.show('Création impossible, un contrat existe déjà sur la période.', 'danger');
      } else {
        this.toastService.show(`Une erreur est survenue lors de la création`, 'danger');
      }
    }
  }

  public async updateContract(toSaveContract: ContractDto) {
    try {
      await this.contractsService.saveContract(toSaveContract);
      this.toastService.show('Le contrat a été modifié avec succès.', 'success');
      this.closeModal.emit();
    } catch (error) {
      if (error.status === 473) {
        this.toastService.show(`Modification impossible, un contrat non terminé existe déjà sur la période.`, 'danger');
      } else if (error.status === 474) {
        this.toastService.show('Modification impossible, un contrat existe déjà sur la période.', 'danger');
      } else if (error.status === 475) {
        this.toastService.show(`Le contrat n'est pas modifiable`, 'danger');
      } else {
        this.toastService.show(`Une erreur est survenue lors de la sauvegarde`, 'danger');
      }
    }
  }

  public async delete(): Promise<void> {
    const confirm: boolean = await this.openConfirmationModal();
    if (confirm) {
      this.closeModal.emit();
      await this.contractsService.delete(this.contract().id);
    }
  }

  public async saveContractAction(ignoreToast: boolean = false): Promise<void> {
    this.submitted = true;
    if (!this.contractForm.valid || (this.actionsForm.items.value?.length && !this.actionsForm.form.valid)) {
      this.toastService.show('La sauvegarde a échoué, veuillez vérifier les données.', 'danger');
      return;
    }

    const toSaveContract = new ContractDto(
      this.contract().id,
      this.contract().beginDate,
      this.contractForm.controls.endDate.value || null,
      this.beneficiary().id,
      this.contract().contractType,
      this.contract().status,
      {
        quarterlyDeclarationResources: this.contract().quarterlyDeclarationResources,
        professionalResources: this.contract().professionalResources,
        otherResources: this.contract().otherResources,
        verificationFamilySituation: this.contract().verificationFamilySituation,
        verificationCAFAddress: this.contract().verificationCAFAddress,
        rsaIncreased: this.contract().rsaIncreased,
        beneficiaryStatus: this.contract().beneficiaryStatus,
        actions: this.contract().actions.map((action, index) => {
          const actionForm: Partial<ContractActionDto> = this.actionsForm.items.value[index];
          return {
            id: action.id,
            constraintId: action.constraintId,
            actionTypeId: action.actionTypeId,
            deadline: action.deadline,
            fulfilled: actionForm.fulfilled,
            place: action.place,
            unfulfilledDetail: actionForm.unfulfilledDetail,
          };
        }),
        comment: this.contract().comment,
        assessmentId: this.contract().assessmentId,
      },
    );
    try {
      await this.contractsService.saveContractActions(toSaveContract);
      if (!ignoreToast) {
        this.closeModal.emit();
        this.toastService.show('Le contrat a été modifié avec succès.', 'success');
      }
    } catch (error) {
      if (ignoreToast) throw error;
      if (error.status === 475) {
        this.toastService.show(`Le contrat n'est pas modifiable`, 'danger');
      } else {
        this.toastService.show(`Une erreur est survenue lors de la sauvegarde`, 'danger');
      }
    }
  }

  // *********************************************************************************************
  // * WKF-ACTION WKF-ACTION WKF-ACTION WKF-ACTION WKF-ACTION WKF-ACTION WKF-ACTION WKF-ACTION W *
  // * WKF-ACTION WKF-ACTION WKF-ACTION WKF-ACTION WKF-ACTION WKF-ACTION WKF-ACTION WKF-ACTION W *
  // *********************************************************************************************

  public async workflowAction(status: ContractEngagementTraceabilityStatus) {
    switch (status) {
      case ContractEngagementTraceabilityStatus.SignedBeneficiary: {
        const signatureDataURL: string = await this.openSignatureModal();
        if (signatureDataURL) {
          await this.updateStatus(
            new TraceabilityDto(this.contract().id, ContractEngagementTraceabilityStatus.SignedBeneficiary, undefined, signatureDataURL),
            'Signatures apposées, contrat maintenant en validation Territoire.',
            this.contract().contractType,
          );
          await this.contractsService.generateContratEngagementDocument(this.contract().id);
        }
        break;
      }
      case ContractEngagementTraceabilityStatus.Unvalidated: {
        let comment: string | null = '';
        if (this.contract().status !== ContractEngagementTraceabilityStatus.Done) {
          comment = await this.openCommentModal();
        }
        if (comment !== null) {
          await this.updateStatus(
            new TraceabilityDto(this.contract().id, ContractEngagementTraceabilityStatus.Unvalidated, comment, undefined),
            'Action réalisée avec succès.',
            this.contract().contractType,
          );
        }
        break;
      }
      case ContractEngagementTraceabilityStatus.Done:
        if (this.permisions.includes((this.workflowedContractPermissionContext + '_CAN_EDIT_ACTION') as any)) {
          await this.saveContractAction(true);
        }
        await this.updateStatus(
          new TraceabilityDto(this.contract().id, status, undefined, undefined),
          'Action réalisée avec succès.',
          this.contract().contractType,
        );
        break;
      default: {
        const isContractGenerated: boolean =
          (status === ContractEngagementTraceabilityStatus.SignedDie &&
            this.contract().status === ContractEngagementTraceabilityStatus.ValidatedDie) ||
          (status === ContractEngagementTraceabilityStatus.SignedTerritory &&
            this.contract().status === ContractEngagementTraceabilityStatus.ValidatedTerritory);
        await this.updateStatus(
          new TraceabilityDto(this.contract().id, status, undefined, undefined),
          isContractGenerated ? 'Contrat généré avec succès.' : 'Action réalisée avec succès.',
          this.contract().contractType,
        );
        break;
      }
    }
  }

  private async updateStatus(traceabilityDto: TraceabilityDto, success: string, contractType: string): Promise<void> {
    try {
      await this.contractsService.updateStatus(traceabilityDto, contractType);
      this.closeModal.emit();
      this.toastService.show(success, 'success');
    } catch (error) {
      if (error.status === 438) {
        this.toastService.show('Erreur durant la génération du contrat. Des signatures sont manquantes.', 'danger');
      } else if (error.status === 440) {
        this.toastService.show;
      } else if (error.status === 437) {
        this.toastService.show("Vous n'avez pas l'autorisation d'effectuer cette action.", 'danger');
      } else if (error.status === 461) {
        this.toastService.show('Erreur durant la génération du contrat. Le dossier du bénéficiaire est incomplet.', 'danger');
      } else if (error.status === 473) {
        this.toastService.show(
          `Modification impossible, un contrat ${ContractEngagementType[contractType]} non terminé existe déjà sur la période.`,
          'danger',
        );
      } else if (error.status === 439) {
        this.toastService.show(`Impossible de clore le contrat. Des champs requis ne sont pas renseignés.`, 'danger');
      } else {
        this.toastService.show(`Erreur lors de la modification des contrats.`, 'danger');
      }
    }
  }

  public async reopenContract() {
    this.workflowAction(
      this.contract().traceability.find((traceability) =>
        [
          ContractEngagementTraceabilityStatus.Unvalidated,
          ContractEngagementTraceabilityStatus.SignedDie,
          ContractEngagementTraceabilityStatus.SignedTerritory,
        ].includes(traceability.status as ContractEngagementTraceabilityStatus),
      ).status as ContractEngagementTraceabilityStatus,
    );
  }

  public async generateContratEngagementDocument() {
    await this.contractsService.generateContratEngagementDocument(this.contract().id);
  }

  // *********************************************************************************************
  // * MODAL MODAL MODAL MODAL MODAL MODAL MODAL MODAL MODAL MODAL MODAL MODAL MODAL MODAL MODAL *
  // * MODAL MODAL MODAL MODAL MODAL MODAL MODAL MODAL MODAL MODAL MODAL MODAL MODAL MODAL MODAL *
  // *********************************************************************************************

  private async openConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return await modalComponent.confirm('Êtes-vous sûr(e) de vouloir supprimer ce contrat ?');
  }

  private async openSignatureModal(): Promise<any> {
    const modal: NgbModalRef = this.modalService.open(SignatureComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md',
    });
    const modalComponent: SignatureComponent = modal.componentInstance as SignatureComponent;
    modalComponent.title = 'Signature bénéficiaire';
    return modalComponent.confirm();
  }

  private async openCommentModal(): Promise<any> {
    const modal: NgbModalRef = this.modalService.open(CommentComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md',
    });
    const modalComponent: CommentComponent = modal.componentInstance as CommentComponent;
    return modalComponent.confirm();
  }
}
